import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNewspaper } from '@fortawesome/free-solid-svg-icons';

import {
  setFlashMessage,
  useWebsite
} from '../contexts/WebsiteContext';
import config from '../config/config';
import PressItem from '../components/PressItem';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';

const { apiUrl } = config;
const apiPath = `${apiUrl}press`;

function PressList({ refreshList }) {

  const [items, setItems] = useState([]);

  const { isModeAdmin } = useMode();
  const { setIsLoading, website } = useWebsite();
  const { token } = useAuth();
  const { t } = useTranslation();

  /* ================= FETCH ================= */

  const fetchItems = useCallback(async () => {
    if (!website) return;

    setIsLoading(true);

    try {
      const response = await axios.get(
        `${apiPath}?website=${website}`
      );

      setItems(response.data || []);

    } catch (error) {
      setFlashMessage(
        t('ERROR_MESSAGE'),
        'error'
      );
      console.error(
        'Error fetching press items:',
        error
      );
    }

    setIsLoading(false);

  }, [website, setIsLoading, t]);

  /* ================= EFFECT ================= */

  useEffect(() => {
    fetchItems();
  }, [fetchItems, refreshList]);

  /* ================= DELETE ================= */

  const handleDelete = async (itemId) => {

    setIsLoading(true);

    try {
      await axios.delete(
        `${apiPath}/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      await fetchItems();

      setFlashMessage(
        t('SUCCESS_DELETED_MESSAGE'),
        'success'
      );

    } catch (error) {
      setFlashMessage(
        t('ERROR_MESSAGE'),
        'error'
      );

      console.error(
        'Delete error:',
        error
      );
    }

    setIsLoading(false);
  };

  /* ================= RANKING ================= */

  const handleRankingUpdate = async (
    direction,
    itemId
  ) => {

    setIsLoading(true);

    try {
      await axios.put(
        `${apiPath}/${itemId}`,
        { ranking: direction },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      await fetchItems();

      setFlashMessage(
        t('SUCCESS_UPDATED_MESSAGE'),
        'success'
      );

    } catch (error) {
      setFlashMessage(
        t('ERROR_MESSAGE'),
        'error'
      );

      console.error(
        `Ranking update error (${direction}):`,
        error
      );
    }

    setIsLoading(false);
  };

  /* ================= EDIT ================= */

  const handleEdit = async (
    itemId,
    editedPress
  ) => {

    setIsLoading(true);

    try {
      await axios.put(
        `${apiPath}/${itemId}`,
        editedPress,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      await fetchItems();

      setFlashMessage(
        t('SUCCESS_UPDATED_MESSAGE'),
        'success'
      );

    } catch (error) {
      setFlashMessage(
        t('ERROR_MESSAGE'),
        'error'
      );

      console.error(
        'Edit error:',
        error
      );
    }

    setIsLoading(false);
  };

  /* ================= RENDER ================= */

  return (
    <>
      {(isModeAdmin ||
        (items && items.length > 0)) && (
        <h2>
          <FontAwesomeIcon icon={faNewspaper} />
          {' '}
          {t('PRESS_TITLE')}
        </h2>
      )}

      {isModeAdmin &&
        items.length === 0 && (
        <p>
          {t('NO_ITEMS')}
        </p>
      )}

      {items.map((press, index) => (
        <PressItem
          key={press.id}
          press={press}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          handleRankingUpdate={
            handleRankingUpdate
          }
          isFirst={index === 0}
          isLast={
            index === items.length - 1
          }
        />
      ))}
    </>
  );
}

export default PressList;